import React from "react";

import { Col, Form, Row } from "react-bootstrap";

import { type Control, Controller, type FieldErrors } from "react-hook-form";

import { AmpType, ParticipantTypes } from "@api/graphql";
import CountryDropdown from "@components/form/CountryDropdown";
import { FormErrorText } from "@components/form/FormErrorText";
import MultiSelectDropdown from "@components/form/MultiSelectDropdown";
import StateDropdown from "@components/form/StateDropdown";
import { mandatoryFields } from "@pages/register/hooks/errorResolver";
import { useIsInternalUserCreatingAccount } from "@pages/register/hooks/useIsInternalUserCreatingAccount";
import type { UserDetailsInput } from "@pages/register/steps/RegisterFormTypes";
import { formatEnumLabel, years } from "@util/index";

import styles from "./index.module.scss";

const AmpTypeOptions = Object.values(AmpType).map((type) => ({
  value: type,
  label: formatEnumLabel(type),
}));

interface AccountDetailsFormProps {
  control: Control<UserDetailsInput>;
  errors: FieldErrors<UserDetailsInput>;
  isArtist?: boolean;
  isBeneficiary?: boolean;
  isAmp: boolean;
  showASTI: boolean;
  isEditing?: boolean;
  isCMO: boolean;
  isArtCentre: boolean;
  isAccountActive?: boolean;
  accountStatus?: string;
  participantTypes?: ParticipantTypes[];
}

export const AccountDetailsForm: React.FC<AccountDetailsFormProps> = ({
  control,
  errors,
  isArtist,
  isBeneficiary,
  isAmp,
  showASTI,
  isEditing,
  isAccountActive,
  accountStatus,
  participantTypes,
}) => {
  const isInternalUserCreatingAccount = useIsInternalUserCreatingAccount();

  return (
    <div className={styles["form-section"]}>
      <div className={styles["form-sub-section"]}>
        <Form.Label className="pb-0">
          {mandatoryFields.accountDetails.address.address_line_1({
            isInternalUserCreatingAccount,
            isAccountActive,
            accountStatus,
            participantTypes,
          }) && "* "}
          Address
        </Form.Label>
        <Row>
          <Col md={12} sm={12}>
            <Controller
              name="account_details.address.address_line_1"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  isInvalid={!!errors?.account_details?.address?.address_line_1}
                  type="text"
                  placeholder="Address line 1"
                />
              )}
            />
            <FormErrorText error={errors?.account_details?.address?.address_line_1} />
          </Col>
        </Row>
        <Row>
          <Col md={12} sm={12}>
            <Controller
              name="account_details.address.address_line_2"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  isInvalid={!!errors?.account_details?.address?.address_line_1}
                  type="text"
                  placeholder="Address line 2"
                />
              )}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12} sm={12}>
            <Controller
              name="account_details.address.address_line_3"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  isInvalid={!!errors?.account_details?.address?.address_line_1}
                  type="text"
                  placeholder="Address line 3"
                />
              )}
            />
          </Col>
        </Row>

        <Row>
          <Col md={12} sm={12}>
            <Form.Label>
              {mandatoryFields.accountDetails.address.city({
                isInternalUserCreatingAccount,
                isAccountActive,
                accountStatus,
                participantTypes,
              }) && "* "}
              Suburb / City
            </Form.Label>
            <Controller
              name="account_details.address.city"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  isInvalid={!!errors?.account_details?.address?.city}
                  type="text"
                  placeholder="Suburb / City"
                />
              )}
            />
            <FormErrorText error={errors?.account_details?.address?.city} />
          </Col>
        </Row>

        <Row>
          <Col md={6} sm={12}>
            <Form.Group>
              <StateDropdown
                fieldName="account_details.address.state"
                control={control}
                isInternalUserCreatingAccount={isInternalUserCreatingAccount}
                isDisabled={false}
                isInvalid={!!errors?.account_details?.address?.state}
                isAccountActive={isAccountActive}
                accountStatus={accountStatus}
                participantTypes={participantTypes}
              />
              <FormErrorText error={errors?.account_details?.address?.state} />
            </Form.Group>
          </Col>
          <Col md={6} sm={12}>
            <Form.Group>
              <Form.Label>
                {mandatoryFields.accountDetails.address.postcode({
                  isInternalUserCreatingAccount,
                  isAccountActive,
                  accountStatus,
                  participantTypes,
                }) && "* "}
                Postcode
              </Form.Label>
              <Controller
                name="account_details.address.postcode"
                control={control}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    isInvalid={!!errors?.account_details?.address?.postcode}
                    type="text"
                    placeholder="Postcode"
                    onWheel={(event) => event.currentTarget.blur()}
                  />
                )}
              />
              <FormErrorText error={errors?.account_details?.address?.postcode} />
            </Form.Group>
          </Col>
        </Row>
        <Row md={12} sm={12}>
          <Col>
            <Form.Group>
              <CountryDropdown
                fieldName="account_details.address.country"
                control={control}
                isRequired
                isDisabled={false}
                isInvalid={!!errors?.account_details?.address?.country}
              />
            </Form.Group>
            <FormErrorText error={errors?.account_details?.address?.country} />
          </Col>
          {(isArtist || isBeneficiary) && (
            <Col>
              <Form.Group>
                <Form.Label>Year of Birth</Form.Label>
                <Controller
                  name="account_details.year_of_birth"
                  control={control}
                  render={({ field }) => (
                    <Form.Select
                      {...field}
                      value={field.value || ""}
                      isInvalid={!!errors?.account_details?.year_of_birth}
                      className={field.value ? "custom-select-value-selected" : ""}
                    >
                      <option hidden value="">
                        Please select
                      </option>
                      {years.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                />
                <FormErrorText error={errors?.account_details?.year_of_birth} />
              </Form.Group>
            </Col>
          )}
        </Row>
      </div>

      {showASTI && (
        <Row>
          <Col md={12} sm={12}>
            <Form.Group>
              <Form.Label>Do you identify as Aboriginal or Torres Strait Islander?</Form.Label>
              <Controller
                name="account_details.is_indigenous"
                control={control}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    as="select"
                    isInvalid={!!errors?.account_details?.is_indigenous}
                    className={field.value ? "custom-select-value-selected" : ""}
                  >
                    <option hidden value="">
                      Please select
                    </option>
                    <option value="YES">Yes</option>
                    <option value="NO">No</option>
                    <option value="PREFER_NOT_TO_SAY">Prefer not to say</option>
                  </Form.Control>
                )}
              />
              <FormErrorText error={errors?.account_details?.is_indigenous} />
            </Form.Group>
          </Col>
        </Row>
      )}

      <Row>
        <Col md={12} sm={12}>
          <Form.Group>
            <Form.Label>Website</Form.Label>
            <Controller
              name="account_details.website"
              control={control}
              render={({ field }) => <Form.Control {...field} type="text" placeholder="Enter your website" />}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={12} sm={12}>
          <Form.Group>
            <Form.Label>Socials</Form.Label>
            <Controller
              name="account_details.socials"
              control={control}
              render={({ field }) => <Form.Control {...field} type="text" placeholder="Enter your socials" />}
            />
          </Form.Group>
        </Col>
      </Row>

      {isAmp && !isEditing && (
        <Row>
          <Col md={12} sm={12}>
            <Form.Group>
              <Form.Label>Select the categories that apply to the organisation</Form.Label>
              <Controller
                name="account_details.amp_type"
                control={control}
                render={({ field }) => (
                  <MultiSelectDropdown field={field} options={AmpTypeOptions} placeholder="Please select" />
                )}
              />
            </Form.Group>
          </Col>
        </Row>
      )}
      {isEditing && (
        <>
          <Row>
            <Col md={12} sm={12}>
              <Form.Group>
                <Form.Label>Account Status</Form.Label>
                <Controller
                  name="account_details.status"
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="text"
                      placeholder="Account Status"
                      className={styles["disabled-field"]}
                      disabled
                    />
                  )}
                  disabled
                />
              </Form.Group>
            </Col>
          </Row>

          {(isArtist || isBeneficiary) && (
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Year of Death</Form.Label>
                  <Controller
                    name="account_details.year_of_death"
                    control={control}
                    render={({ field }) => (
                      <Form.Select
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value || null)}
                        className={field.value ? "custom-select-value-selected" : ""}
                      >
                        <option value="">Select Year</option>
                        {years.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  />
                  <FormErrorText error={errors?.account_details?.year_of_death} />
                </Form.Group>
              </Col>
            </Row>
          )}
        </>
      )}
    </div>
  );
};
